import {
  KalturaAsset,
  KalturaAssetType,
  KalturaDynamicOrderBy,
  KalturaFilterPager,
  KalturaMetaTagOrderBy,
  KalturaRecordingAsset,
  KalturaSearchAssetFilter,
} from '@kaltura-ott/ott-client-types/api/types';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { map, mergeMap, switchMap, takeUntil } from 'rxjs/operators';

import { KuxContinueWatchingRailArg } from '../interfaces';
import { KuxMediaAssetType, KuxProgramType } from '../interfaces/asset';
import { KuxBookmark } from '../interfaces/bookmarks';

import {
  getAssetHistoryList,
  getAssetList,
  getAssetsByChannelId,
  getMediaAssetsByAssetHistory,
  getRecommendationAssets,
} from './asset';
import { getMediaAssetsByIdList } from './asset-v2';
import { assetsToType, assetToType } from './asset-v2.helper';
import { getBookmarks } from './bookmarks';
import { assetHistoryToKuxBookmark } from './bookmarks.helper';
import { addKsql } from './category.tree.helper';
import { ExtendedMediaType, KuXDms, MediaType } from './dms';
import { getEpgDataV2 } from './epg-v2';
import { subscriptionsGetPpvEntitlements } from './subscriptions';
import { endOfDay, startOfDay } from './utils/date';

export interface KuxList<A> {
  totalCount: number;
  items: A[];
}

interface KuxCollectionRailArg {
  channelId: number;
  pageIndex?: number;
  pageSize?: number;
  kSql?: string;
  groupBy?: string;
  orderBy?: string;
  isGroupedGrid?: boolean;
}

/**
 *  Get collection rail
 */
export const getCollectionRailV2 = <T extends KuxMediaAssetType>({
  channelId,
  pageIndex = 1,
  pageSize = 10,
  kSql = '',
  groupBy = '',
  orderBy = '',
  isGroupedGrid = false,
}: KuxCollectionRailArg): Observable<KuxList<T>> => {
  return getAssetsByChannelId({ channelId, pageIndex, pageSize, kSql, groupBy, orderBy }).pipe(
    map((assetList) => {
      const assets = assetList.objects;
      const items = assetsToType<KalturaAsset, T>(assets, { isGroupedGrid });
      return {
        totalCount: assetList.totalCount,
        items,
      } as KuxList<T>;
    }),
  );
};

interface KuxCollectionRailArg {
  channelId: number;
  pageIndex?: number;
  pageSize?: number;
  kSql?: string;
  groupBy?: string;
  handleError?: boolean;
}

const MIN_PAGE_SIZE_ASSET_HISTORY = 5;

export const getContinueWatchingRailV2 = <T extends KuxMediaAssetType & { bookmark: KuxBookmark }>({
  daysLessThanOrEqual,
  kSql = '',
  assetHistoryKSQL = '',
  groupBy = '',
  statusEqual,
  typeIn,
  suppress,
  pageIndex = 1,
  pageSize = 10,
  isGroupedGrid = false,
}: KuxContinueWatchingRailArg): Observable<KuxList<T>> => {
  return getAssetHistoryList({
    statusEqual,
    pageIndex,
    pageSize: Math.max(pageSize, MIN_PAGE_SIZE_ASSET_HISTORY),
    typeIn,
    suppress,
    daysLessThanOrEqual,
    groupBy,
    // @ts-expect-error TODO Fix types
    assetHistoryKSQL,
  }).pipe(
    mergeMap((assetHistoryList) => {
      if (!assetHistoryList.objects?.length) {
        return of({ totalCount: 0, items: [] });
      }
      return getMediaAssetsByAssetHistory(assetHistoryList.objects, kSql, typeIn).pipe(
        map((assets) => {
          const items = assetHistoryList.objects
            // @ts-expect-error TODO Fix
            ?.map((assetHistory) => {
              const asset = assets.objects.find((asset) => {
                if (
                  (assetHistory.assetType === KalturaAssetType.epg ||
                    assetHistory.assetType === KalturaAssetType.media) &&
                  !(asset instanceof KalturaRecordingAsset)
                ) {
                  return assetHistory.assetId === asset.id;
                } else {
                  // @ts-expect-error TODO Fix types
                  return assetHistory.assetId === Number(asset.recordingId);
                }
              });
              if (!asset) {
                return false;
              }
              const type = assetToType<KalturaAsset, T>(asset, { isGroupedGrid });
              if (!type) {
                return false;
              }
              return {
                ...type,
                bookmark: assetHistoryToKuxBookmark(assetHistory),
              };
            })
            .filter(Boolean);

          return {
            totalCount: assetHistoryList.totalCount,
            items,
          };
        }),
      );
    }),
  );
};

interface KuxCurrentProgramByLinearListArg {
  linearIdList: number[];
  liveTimestamp: number | undefined;
  allDayEpg?: boolean;
  loadMosaicPrograms?: boolean;
}
export const getCurrentProgramByLinearList = ({
  linearIdList,
  liveTimestamp = Date.now(),
  allDayEpg = true,
  loadMosaicPrograms = false,
}: KuxCurrentProgramByLinearListArg): Observable<KuxProgramType[]> => {
  return getEpgDataV2(
    {
      startTime: startOfDay(new Date(liveTimestamp)),
      endTime: endOfDay(new Date(liveTimestamp)),
      // linear assets ids should be sorted for proper caching on BE side
      linearIdList: linearIdList.sort(),
      loadMosaicPrograms,
    },
    true,
    liveTimestamp,
    allDayEpg,
  );
};

interface KuxRecommendationRailV2Arg {
  pageSize?: number;
  pageIndex?: number;
  kSql: string;
  partnerListTypeIn?: number;
  groupBy?: string;
  filterWatched?: boolean;
  orderBy?: string;
  isGroupedGrid?: boolean;
}
export const getRecommendationRailV2 = <T extends KuxMediaAssetType>({
  pageSize,
  pageIndex,
  kSql,
  groupBy,
  partnerListTypeIn,
  filterWatched = true,
  orderBy,
  isGroupedGrid = false,
}: KuxRecommendationRailV2Arg): Observable<KuxList<T>> => {
  if (!partnerListTypeIn) {
    return of({
      totalCount: 0,
      items: [],
    });
  }
  // @ts-expect-error TODO Fix types
  return getRecommendationAssets({ pageIndex, pageSize, kSql, partnerListTypeIn, groupBy, orderBy }).pipe(
    map((assetsResponse) => {
      return {
        totalCount: assetsResponse.totalCount,
        items: assetsToType<KalturaAsset, T>(assetsResponse.objects, { isGroupedGrid }),
      };
    }),
    switchMap(({ totalCount, items }) => {
      if (filterWatched) {
        return forkJoin({
          epgBookmark: getBookmarks(items?.map((item) => item?.id).join(',')),
          mediaBookmark: getBookmarks(items?.map((item) => item?.id).join(','), KalturaAssetType.media),
        }).pipe(
          map(({ epgBookmark, mediaBookmark }) => {
            const filteredItems = items.filter(
              (item) =>
                // @ts-expect-error TODO Fix
                !epgBookmark?.objects?.map((bookmark) => Number(bookmark?.id)).includes(item.id) &&
                // @ts-expect-error TODO Fix
                !mediaBookmark?.objects?.map((bookmark) => Number(bookmark?.id)).includes(item.id),
            );
            return {
              totalCount,
              items: filteredItems,
            } as KuxList<T>;
          }),
        );
      } else {
        return of({ totalCount, items }) as Observable<KuxList<T>>;
      }
    }),
  );
};

interface KuxSeriesRailV2Arg {
  pageSize?: number;
  pageIndex?: number;
  seriesId: string;
  seasonNumber: number;
  assetType: ExtendedMediaType;
  isRecords: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  cancelSubject?: any;
}
export const getSeriesListV2 = ({
  seriesId,
  seasonNumber,
  pageSize,
  pageIndex,
  assetType,
  isRecords,
  cancelSubject = new Subject(),
}: // eslint-disable-next-line @typescript-eslint/no-explicit-any
KuxSeriesRailV2Arg): Observable<KuxList<any> | undefined> => {
  const filterCatchup = `(or start_date>'-${KuXDms.playableCatchupBuffer}' Popular='1')`;

  const order = new KalturaDynamicOrderBy({
    name: 'EpisodeNumber',
    orderBy: KalturaMetaTagOrderBy.metaAsc,
  });

  // @ts-expect-error TODO Fix types
  const filter: KalturaSearchAssetFilter = {
    dynamicOrderBy: order,
  };

  if (assetType == MediaType.Program) {
    filter.kSql = seasonNumber
      ? `(and SeriesId='${seriesId}' SeasonNumber='${seasonNumber}' ${filterCatchup})`
      : `(and SeriesId='${seriesId}' ${filterCatchup})`;
    filter.typeIn = KuXDms.mediaTypes[assetType].toString();
  } else if (isRecords) {
    filter.kSql = seasonNumber
      ? `(and SeriesId='${seriesId}' SeasonNumber='${seasonNumber}' (and asset_type='${assetType}'))`
      : `(and SeriesId='${seriesId}' (and asset_type='${assetType}'))`;
  } else {
    filter.kSql = seasonNumber
      ? `(and SeriesId='${seriesId}' SeasonNumber='${seasonNumber}')`
      : `(and SeriesId='${seriesId}')`;
    // @ts-expect-error TODO Fix
    filter.typeIn = KuXDms.mediaTypes[assetType].toString();
  }

  const pager = new KalturaFilterPager({
    pageIndex,
    pageSize,
  });

  return getAssetList({ filter: new KalturaSearchAssetFilter(filter), pager }, 'SeasonNumber', !isRecords).pipe(
    map((assetList) => {
      return {
        totalCount: assetList.totalCount,
        items: assetList.objects,
      };
    }),
    takeUntil(cancelSubject),
  );
};

interface RentalsRailArgs {
  pageSize: number;
  pageIndex: number;
  kSql: string;
  groupBy: string;
  isGroupedGrid?: boolean;
}

export const getRentalsRailV2 = ({ pageSize, pageIndex, kSql, groupBy, isGroupedGrid = false }: RentalsRailArgs) => {
  const rentalsKsql = addKsql(
    kSql,
    `(and (or asset_type='${KuXDms.mediaTypes[MediaType.Movie]}' asset_type='${
      KuXDms.mediaTypes[MediaType.Episode]
    }'))`,
  );

  const pager = new KalturaFilterPager({
    pageIndex,
    pageSize,
  });

  return subscriptionsGetPpvEntitlements().pipe(
    map((entitlements) => {
      const ids: number[] = [];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      entitlements.objects.map((e: any) => {
        ids.push(e.mediaId);
      });
      return { ids, pager, rentalsKsql };
    }),
    switchMap(({ ids, rentalsKsql, pager }) => {
      return getMediaAssetsByIdList(ids, rentalsKsql, pager, groupBy, isGroupedGrid).pipe(
        map((items) => {
          const itemsMap = items.reduce<Record<number, KuxMediaAssetType>>((acc, it) => {
            if (it) {
              acc[it.id] = it;
            }

            return acc;
          }, {});

          const resItems = ids.reduce<KuxMediaAssetType[]>((acc, id) => {
            if (itemsMap[id]) {
              acc.push(itemsMap[id]);
            }

            return acc;
          }, []);

          return {
            totalCount: resItems.length,
            items: resItems,
          };
        }),
      );
    }),
  );
};
